import { useState, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { Tooltip } from "../../../../../../../../../components/Tooltip/Tooltip";
import { TruncatedTextTooltip } from "../../../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import Dropdown from "../../../../../../../../../components/Dropdown/Dropdown";
import MultiSelectSearchableDropdown from "../../../../../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import SingleDatePicker from "../../../../../../../../../components/SingleDatePicker/SingleDatePicker";
import { getDateTimeByFormat } from "../../../../../../../../../redux/slices/dateTimeFormatSlice";
import { getTimeAgo } from "../../../../../../../../../utils/functions/date-time";
import {
  getExternalVendorOptions,
  PRIORITY_FILTER_OPTIONS,
} from "../../../../AllTickets/utils/constant";
import {
  getCategoryStyle,
  getPriorityColor,
} from "../../../../AllTickets/utils/functions";
import { TicketCardActionMenu } from "../../../../AllTickets/components/TicketCardActionMenu";
import { ShareTicketModal } from "../../../../AllTickets/components/ShareTicketModal";
import DeleteTicketModal from "../../../../AllTickets/components/DeleteTicketModal";
import type { HelpDeskTicket } from "../../../../AllTickets/utils/types";
import { customToast } from "../../../../../../../../../utils/custom-toast/custom-toast";
import { useGetPropertiesListQuery } from "../../../../../../../../../redux/services/playgroundApi";
import {
  useUpdateTicketMutation,
  useDeleteTicketMutation,
  useGetHelpdeskTagsQuery,
  useCreateHelpdeskTagMutation,
  useGetHelpdeskCustomFieldsQuery,
  useGetHelpdeskResolutionReasonsQuery,
  useGetTicketCategoriesQuery,
} from "../../../../../../../../../redux/services/helpdeskApi";

import LeftArrowIcon from "../../../../../../../../../assets/svg/left-arrow-icon";
import HashtagIcon from "../../../../../../../../../assets/svg/hashtag-icon";
import ThreeDotsVerticalIcon from "../../../../../../../../../assets/svg/three-dots-vertical-icon";
import FlaggedIcon from "../../../../../../../../../assets/svg/flagged-icon";
import CalendarIcon from "../../../../../../../../../assets/svg/calendar-icon";
import ClockIcon from "../../../../../../../../../assets/svg/clock-icon";
import KeyAccountManagerIcon from "../../../../../../../../../assets/svg/key-account-manager-icon";
import AddIcon from "../../../../../../../../../assets/svg/add-icon";
import ChevronDownIcon from "../../../../../../../../../assets/svg/chevron-down-icon";
import CrossCircleIcon from "../../../../../../../../../assets/svg/v1/cross-circle-icon";
import { Avatar } from "../../../../../../../../../components/Avatar/Avatar";
import VerifiedIcon from "../../../../../../../../../assets/svg/VerifiedIcon";

import type { CustomFieldType } from "../../../../CreateTicket/utils/types";
import { FEATURE_REQUESTS_SYSTEM_NAME } from "../../../../CreateTicket/utils/constants";

import { TicketData, HelpdeskStage } from "../../../../../utils/types";
import { capitalize, isHelpdeskSupportCategoryKey } from "../utils/constants";
import {
  HelpdeskCustomFieldDefinition,
  buildCustomFieldsValuesFromTicket,
  getChildFieldKeys,
  normalizeHelpdeskFieldType,
  HELPDESK_CUSTOM_FIELD_TYPES,
  serializeTicketCustomFieldsPayload,
} from "../utils/helpdeskCustomFields";
import { HelpdeskTicketDynamicFields } from "./HelpdeskTicketDynamicFields";
import { AddCustomFieldDropdownMenu } from "./AddCustomFieldDropdownMenu";
import { HelpdeskTicketCustomFieldModal } from "./HelpdeskTicketCustomFieldModal";
import { RootState } from "../../../../../../../../../redux/store";
import AgentAssignmentDropdown, {
  type AgentUser,
} from "../../../../../../AgentAssignmentDropdown";
import { useOrgUsersList } from "../../../../../../../../../hooks/useOrgUsersList";
import { isHelpdeskResolutionClient } from "../../../../../utils/function";
import { getClientDomain } from "../../../../../../../../../utils/functions/client-code";
import DueDateAlertModal from "./DueDateAlertModal";
import TicketCategoryChangeModal from "./TicketCategoryChangeModal";
import ResolutionReasonModal from "../../../../AllTickets/components/ResolutionReasonModal";
import SlaDeadlineTag from "../../../utils/SlaDeadlineTag";
import WaitingOnThirdPartyModal from "../../../../AllTickets/components/WaitingOnThirdPartyModal";
import AssigneeRequiredModal from "../../../../AllTickets/components/AssigneeRequiredModal";
import HourglassIcon from "../../../../../../../../../assets/svg/hour-glass-icon";
import { PermissionDisabledWrapper } from "../../../../PermissionDisabledWrapper";
import { useHasHelpdeskUpdateAccess } from "../../../../../utils/helpdeskPermissions";
import { getHelpdeskLabels } from "../../../../../../../../../utils/constants/mappings";

const getCreatedByName = (
  createdBy: TicketData["created_by"] | undefined
): string => {
  if (!createdBy) return "Unassigned";
  if (typeof createdBy === "object") return createdBy?.name || "Unassigned";
  return createdBy || "Unassigned";
};

const hexToRgba = (hex: string | undefined, alpha: number): string => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  const cleaned = hex?.replace("#", "");
  const r = parseInt(cleaned?.substring(0, 2), 16);
  const g = parseInt(cleaned?.substring(2, 4), 16);
  const b = parseInt(cleaned?.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

interface TicketHeaderFormValues {
  priority: string;
  stage_key: string;
  assignees: string[];
  property_name: string;
  ticketCustomFieldValues: Record<string, unknown>;
  tags: string[];
  due_date: string | null;
  resolution_reason: string;
  external_vendor: string;
  ticket_category: string;
}

interface TicketHeaderProps {
  ticketId: string;
  ticketData?: TicketData;
  stagesData?: HelpdeskStage[];
}

const DIRECT_ISSUE_CATEGORY_MAP: Record<string, string> = {
  sales: "Sales",
  marketing: "Marketing",
};

export const TicketHeader = ({
  ticketId,
  ticketData,
  stagesData,
}: TicketHeaderProps) => {
  const navigate = useNavigate();
  const isResolutionEnabled = isHelpdeskResolutionClient(getClientDomain());
  const isSidebarOpen = useSelector((state: RootState) => state.sidebar.isOpen);
  const clientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const authCurrentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const canUpdate = useHasHelpdeskUpdateAccess(ticketData?.ticket_category);

  const [isAssigningAgent, setIsAssigningAgent] = useState(false);
  const [updateTicket] = useUpdateTicketMutation();
  const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();
  const [createHelpdeskTag, { isLoading: isLoadingCreateTagMutation }] =
    useCreateHelpdeskTagMutation();

  const { activeUsers } = useOrgUsersList();
  const { data: propertiesData } = useGetPropertiesListQuery(
    { managerId: authCurrentClientCode ?? "", page: 1, pageSize: 1000 },
    { skip: !authCurrentClientCode }
  );
  const { data: customFieldsData } = useGetHelpdeskCustomFieldsQuery({});
  const { data: ticketCategoriesData } = useGetTicketCategoriesQuery();
  const { data: resolutionReasonsData } = useGetHelpdeskResolutionReasonsQuery(
    {},
    { skip: !isResolutionEnabled }
  );
  const {
    data: helpdeskTagsData,
    refetch: refetchHelpdeskTags,
    isFetching: isFetchingHelpdeskTags,
  } = useGetHelpdeskTagsQuery({});

  const tagsRefetchRef = useRef<ReturnType<typeof refetchHelpdeskTags> | null>(
    null
  );
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const addCustomFieldRef = useRef<HTMLButtonElement>(null);

  const [showDueDateAlertModal, setShowDueDateAlertModal] = useState(false);
  const [pendingPriority, setPendingPriority] = useState<string | null>(null);
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [isSubmittingResolution, setIsSubmittingResolution] = useState(false);
  const [showThirdPartyModal, setShowThirdPartyModal] = useState(false);
  const [isSubmittingThirdParty, setIsSubmittingThirdParty] = useState(false);
  const [showAssigneeRequiredModal, setShowAssigneeRequiredModal] =
    useState(false);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);

  const agentsList: AgentUser[] = useMemo(
    () =>
      activeUsers.map((user) => ({
        id: user?._id,
        name: user?.name,
        email: user?.email,
        role: user?.role,
        status: user?.status,
      })),
    [activeUsers]
  );

  const tagOptions = useMemo(
    () =>
      helpdeskTagsData?.map((tag: { tag_key: string; title: string }) => ({
        value: tag?.tag_key,
        label: tag?.title,
        content: (
          <TruncatedTextTooltip
            text={tag?.title}
            maxWidth="max-w-[300px]"
            className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk"
          />
        ),
      })) ?? [],
    [helpdeskTagsData]
  );

  const propertyOptions = useMemo(
    () =>
      propertiesData?.res?.map((p: { id: number; name: string }) => ({
        key: p?.name,
        label: p?.name,
      })) ?? [],
    [propertiesData]
  );

  const categoryOptions = useMemo(
    () =>
      (ticketCategoriesData ?? [])?.map((cat) => {
        const displayName = capitalize(cat?.display_name ?? "");
        const chipBaseClassName = `inline-flex items-center text-md font-medium px-2 py-0.5 rounded-md border ${getCategoryStyle(displayName)}`;
        return {
          value: cat?.key,
          content: <span className={chipBaseClassName}>{displayName}</span>,
          selectedOptionContent: (
            <span className={`${chipBaseClassName} gap-1.5`}>
              {displayName}
              <ChevronDownIcon className="w-2 h-2 transition-transform duration-300 group-aria-expanded:rotate-180" />
            </span>
          ),
        };
      }),
    [ticketCategoriesData]
  );

  const customFieldDefs = useMemo(
    () => (customFieldsData ?? []) as HelpdeskCustomFieldDefinition[],
    [customFieldsData]
  );

  const systemNameFieldDef = useMemo(
    () => customFieldDefs?.find((f) => f?.key === "system-name") ?? null,
    [customFieldDefs]
  );

  const issueTypeFieldDef = useMemo(
    () => customFieldDefs?.find((f) => f?.key === "issue-type") ?? null,
    [customFieldDefs]
  );

  const resolutionOptions = useMemo(() => {
    const raw = resolutionReasonsData as
      | { reasons?: string[] }
      | string[]
      | undefined;
    const reasons = Array.isArray(raw) ? raw : (raw?.reasons ?? []);
    return reasons.map((reason: string) => ({
      value: reason,
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          {reason}
        </span>
      ),
      selectedOptionContent: (
        <div className="flex items-center gap-1 min-w-0">
          <VerifiedIcon className="w-3.5 h-3.5 text-verba-green-success dark:text-verba-green-success-dk shrink-0" />
          <TruncatedTextTooltip
            text={reason}
            maxWidth="max-w-full min-w-0"
            className="text-md font-medium text-verba-green-success dark:text-verba-green-success-dk"
          />
        </div>
      ),
    }));
  }, [resolutionReasonsData]);

  const initialValues: TicketHeaderFormValues = useMemo(
    () => ({
      priority: ticketData?.priority ?? "",
      stage_key: ticketData?.stage_key ?? "",
      assignees: ticketData?.assignees ?? [],
      property_name: ticketData?.property_name ?? "",
      ticketCustomFieldValues: buildCustomFieldsValuesFromTicket(
        ticketData ?? {},
        customFieldDefs
      ),
      tags: ticketData?.tags ?? [],
      due_date: ticketData?.due_date ?? null,
      resolution_reason: ticketData?.resolution_reason ?? "",
      external_vendor: ticketData?.external_vendor ?? "",
      ticket_category: ticketData?.ticket_category ?? "",
    }),
    [ticketData, customFieldDefs]
  );

  const createdAtDisplayText = useMemo(() => {
    if (!ticketData?.created_at) return "";
    const df = getDateTimeByFormat(new Date(ticketData.created_at));
    return `${df?.date}, ${df?.time}`;
  }, [ticketData?.created_at]);

  const slaDeadlineDisplayText = useMemo(() => {
    if (!ticketData?.due_date) return "Not set";
    return (
      getDateTimeByFormat(new Date(ticketData.due_date))?.date ?? "Not set"
    );
  }, [ticketData?.due_date]);

  const lastUpdatedDisplayText = useMemo(() => {
    if (!ticketData?.updated_at) return "";
    return `${getTimeAgo(ticketData.updated_at)} ago`;
  }, [ticketData?.updated_at]);

  const formik = useFormik<TicketHeaderFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const submitField = useCallback(
    async (fieldName: string, value: unknown) => {
      try {
        await updateTicket({
          ticketId: String(ticketData?.ticket_id ?? ticketId),
          [fieldName]: value,
        })?.unwrap();
        customToast(helpdeskLabels.ticketUpdatedSuccess, "success");
      } catch {
        customToast(helpdeskLabels.failedToUpdateTicket, "error");
      }
    },
    [updateTicket, ticketData?.ticket_id, ticketId]
  );

  const handleFieldChange = useCallback(
    (fieldName: keyof TicketHeaderFormValues, value: unknown) => {
      formik?.setFieldValue(fieldName, value);
      submitField(fieldName, value);
    },
    [formik, submitField]
  );

  /** Single PUT carrying the new category plus the resolved system name / issue type. */
  const applyCategoryChange = useCallback(
    async (categoryKey: string, systemName: string, issueType: string) => {
      const nextValues = {
        ...formik?.values?.ticketCustomFieldValues,
        "system-name": systemName,
        "issue-type": issueType,
      };

      const serializedCustomFields: Record<
        string,
        string | number | boolean | string[] | null
      > = {
        ...serializeTicketCustomFieldsPayload(nextValues, customFieldDefs),
      };

      // serialize drops empty values, so clearing needs an explicit null
      if (!issueType || systemName === FEATURE_REQUESTS_SYSTEM_NAME) {
        serializedCustomFields["issue-type"] = null;
      }

      try {
        await updateTicket({
          ticketId: String(ticketData?.ticket_id ?? ticketId),
          ticket_category: categoryKey,
          custom_fields: serializedCustomFields,
        })?.unwrap();
        formik?.setFieldValue("ticket_category", categoryKey);
        formik?.setFieldValue("ticketCustomFieldValues", nextValues);
        customToast(helpdeskLabels.ticketUpdatedSuccess, "success");
      } catch {
        customToast(helpdeskLabels.failedToUpdateTicket, "error");
      }
    },
    [
      formik,
      customFieldDefs,
      updateTicket,
      ticketData?.ticket_id,
      ticketId,
      helpdeskLabels,
    ]
  );

  /** The ticket's currently selected issue type, if any. */
  const hasSelectedIssueType = Boolean(
    formik?.values?.ticketCustomFieldValues?.["issue-type"]
  );

  const handleTicketCategoryChange = useCallback(
    (val: string) => {
      if (!val || val === formik?.values?.ticket_category) return;

      // Helpdesk Support has no derivable system name — ask for both before saving
      if (isHelpdeskSupportCategoryKey(val, ticketCategoriesData)) {
        setPendingCategory(val);
        return;
      }

      // Sales / Marketing derive the system name, which invalidates the issue
      // type — re-ask only when the ticket actually has one to replace
      const directSystemName = DIRECT_ISSUE_CATEGORY_MAP[val] ?? "";
      if (directSystemName && hasSelectedIssueType) {
        setPendingCategory(val);
        return;
      }

      applyCategoryChange(val, directSystemName, "");
    },
    [
      formik?.values?.ticket_category,
      ticketCategoriesData,
      hasSelectedIssueType,
      applyCategoryChange,
    ]
  );

  const handleConfirmCategoryChange = useCallback(
    async ({
      systemName,
      issueType,
    }: {
      systemName: string;
      issueType: string;
    }) => {
      if (!pendingCategory) return;
      setIsUpdatingCategory(true);
      try {
        await applyCategoryChange(pendingCategory, systemName, issueType);
      } finally {
        setIsUpdatingCategory(false);
        setPendingCategory(null);
      }
    },
    [pendingCategory, applyCategoryChange]
  );

  const handleCancelCategoryChange = useCallback(() => {
    setPendingCategory(null);
  }, []);

  const pendingCategoryDisplayName = useMemo(
    () =>
      ticketCategoriesData?.find((cat) => cat?.key === pendingCategory)
        ?.display_name ?? "",
    [ticketCategoriesData, pendingCategory]
  );

  const pendingForcedSystemName = pendingCategory
    ? (DIRECT_ISSUE_CATEGORY_MAP[pendingCategory] ?? null)
    : null;

  const handlePriorityChange = useCallback(
    (value: string) => {
      if (formik?.values?.priority !== value) {
        setPendingPriority(value);
        setShowDueDateAlertModal(true);
      }
    },
    [formik?.values?.priority]
  );

  const handleSubmitPriority = useCallback(async () => {
    if (!pendingPriority) return;
    try {
      setIsUpdatingPriority(true);
      await submitField("priority", pendingPriority);
      formik?.setFieldValue("priority", pendingPriority);
    } catch {
      //no-op
    } finally {
      setIsUpdatingPriority(false);
      setPendingPriority(null);
      setShowDueDateAlertModal(false);
    }
  }, [formik, submitField, pendingPriority]);

  const handleCancelPriority = useCallback(() => {
    setPendingPriority(null);
    setShowDueDateAlertModal(false);
  }, []);

  const handleStageChange = useCallback(
    (value: string) => {
      if (value === formik?.values?.stage_key) return;
      if (value === "closed" && !formik?.values?.assignees?.length) {
        setShowAssigneeRequiredModal(true);
        return;
      }
      if (value === "waiting-on-third-party" && isResolutionEnabled) {
        setShowThirdPartyModal(true);
        return;
      }
      if (value === "closed" && isResolutionEnabled) {
        setShowResolutionModal(true);
        return;
      }
      handleFieldChange("stage_key", value);
    },
    [
      handleFieldChange,
      formik?.values?.stage_key,
      formik?.values?.assignees,
      isResolutionEnabled,
    ]
  );

  const handleResolutionSubmit = useCallback(
    async (resolutionReason: string) => {
      setIsSubmittingResolution(true);
      try {
        await updateTicket({
          ticketId: String(ticketData?.ticket_id ?? ticketId),
          stage_key: "closed",
          resolution_reason: resolutionReason,
        })?.unwrap();
        formik?.setFieldValue("stage_key", "closed");
        formik?.setFieldValue("resolution_reason", resolutionReason);
        customToast(helpdeskLabels.ticketUpdatedSuccess, "success");
      } catch {
        customToast(helpdeskLabels.failedToUpdateTicket, "error");
      } finally {
        setIsSubmittingResolution(false);
        setShowResolutionModal(false);
      }
    },
    [updateTicket, ticketData?.ticket_id, ticketId, formik]
  );

  const handleResolutionCancel = useCallback(() => {
    setShowResolutionModal(false);
  }, []);

  const handleThirdPartySubmit = useCallback(
    async (externalVendor: string) => {
      setIsSubmittingThirdParty(true);
      try {
        await updateTicket({
          ticketId: String(ticketData?.ticket_id ?? ticketId),
          stage_key: "waiting-on-third-party",
          external_vendor: externalVendor,
        })?.unwrap();
        formik?.setFieldValue("stage_key", "waiting-on-third-party");
        formik?.setFieldValue("external_vendor", externalVendor);
        customToast(helpdeskLabels.ticketUpdatedSuccess, "success");
      } catch {
        customToast(helpdeskLabels.failedToUpdateTicket, "error");
      } finally {
        setIsSubmittingThirdParty(false);
        setShowThirdPartyModal(false);
      }
    },
    [updateTicket, ticketData?.ticket_id, ticketId, formik]
  );

  const handleThirdPartyCancel = useCallback(() => {
    setShowThirdPartyModal(false);
  }, []);

  const handleAssigneeRequiredClose = useCallback(() => {
    setShowAssigneeRequiredModal(false);
  }, []);

  const handleAssignAgent = useCallback(
    async (agentIds: string[], _agents: AgentUser[]) => {
      try {
        setIsAssigningAgent(true);
        await submitField("assignees", agentIds);
      } catch (error) {
        console.log(error);
      } finally {
        setIsAssigningAgent(false);
      }
    },
    [submitField]
  );

  const handleAddTag = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery?.trim()) return;
      const title = searchQuery.trim();
      try {
        const result = await createHelpdeskTag({ title }).unwrap();
        const tagKey = result?.tag_key ?? title;
        const currentTags = formik?.values?.tags ?? [];
        if (!currentTags.includes(tagKey)) {
          const newTags = [...currentTags, tagKey];
          formik?.setFieldValue("tags", newTags);
          submitField("tags", newTags);
        }
        tagsRefetchRef.current?.abort();
        tagsRefetchRef.current = refetchHelpdeskTags();
      } catch {
        customToast("Failed to create tag", "error");
      }
    },
    [formik, submitField, createHelpdeskTag, refetchHelpdeskTags]
  );

  const handleTicketCustomFieldChange = useCallback(
    async (fieldKey: string, value: unknown) => {
      const childKeys = getChildFieldKeys(fieldKey, customFieldDefs);
      const nextValues = {
        ...formik?.values?.ticketCustomFieldValues,
        [fieldKey]: value,
      };
      for (const ck of childKeys) {
        const ch = customFieldDefs?.find((d) => d?.key === ck);
        const nt = normalizeHelpdeskFieldType(ch?.type ?? "");
        nextValues[ck] =
          nt === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI
            ? []
            : nt === HELPDESK_CUSTOM_FIELD_TYPES.CHECKBOX
              ? false
              : nt === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_SINGLE ||
                  nt === HELPDESK_CUSTOM_FIELD_TYPES.RADIO
                ? null
                : "";
      }

      const isSwitchingToFeatureRequest =
        fieldKey === "system-name" && value === FEATURE_REQUESTS_SYSTEM_NAME;

      if (isSwitchingToFeatureRequest) {
        nextValues["issue-type"] = null;
      }

      formik?.setFieldValue("ticketCustomFieldValues", nextValues);

      const serializedCustomFields: Record<
        string,
        string | number | boolean | string[] | null
      > = {
        ...serializeTicketCustomFieldsPayload(nextValues, customFieldDefs),
      };

      if (isSwitchingToFeatureRequest) {
        serializedCustomFields["issue-type"] = null;
      }

      if (
        Object.keys(serializedCustomFields).length === 0 &&
        !isSwitchingToFeatureRequest
      ) {
        return;
      }

      try {
        await updateTicket({
          ticketId: String(ticketData?.ticket_id ?? ticketId),
          custom_fields: serializedCustomFields,
          ...(isSwitchingToFeatureRequest ? { priority: null } : {}),
        })?.unwrap();

        if (isSwitchingToFeatureRequest) {
          formik?.setFieldValue("priority", "");
        }

        customToast(helpdeskLabels.ticketUpdatedSuccess, "success");
      } catch {
        customToast(helpdeskLabels.failedToUpdateTicket, "error");
      }
    },
    [formik, customFieldDefs, ticketData?.ticket_id, ticketId, updateTicket]
  );

  const selectedStage = stagesData?.find(
    (s) => s?.key === formik?.values?.stage_key
  );

  const [showShareModal, setShowShareModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCustomFieldMenu, setShowCustomFieldMenu] = useState(false);
  const [customFieldModal, setCustomFieldModal] = useState<{
    open: boolean;
    fieldType: CustomFieldType | null;
  }>({ open: false, fieldType: null });

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const handleMoreOptionsClick = () => {
    setShowActionMenu((prev) => !prev);
  };

  const handleCustomFieldSelect = (fieldType: CustomFieldType) => {
    setShowCustomFieldMenu(false);
    setCustomFieldModal({ open: true, fieldType });
  };

  const handleDeleteTicket = () => {
    setShowActionMenu(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTicket(String(ticketData?.ticket_id ?? ticketId))?.unwrap();
      customToast(helpdeskLabels.ticketDeletedSuccess, "success");
      setShowDeleteModal(false);
      navigate(-1);
    } catch {
      customToast(helpdeskLabels.failedToDeleteTicket, "error");
    }
  };

  const [ticketIdCopied, setTicketIdCopied] = useState(false);

  const handleCopyTicketId = () => {
    const id = String(ticketData?.ticket_id ?? ticketId);
    navigator.clipboard.writeText(id);
    setTicketIdCopied(true);
    setTimeout(() => setTicketIdCopied(false), 2000);
  };

  const handleScrollToggle = useCallback((lock: boolean) => {
    const container = document.querySelector(
      "[data-ticket-header-scroll]"
    ) as HTMLElement | null;
    if (container) {
      container.style.overflowY = lock ? "hidden" : "auto";
    }
  }, []);

  const vendorOptions = useMemo(
    () =>
      getExternalVendorOptions(clientCode).map((vendor) => ({
        value: vendor,
        selectedOptionContent: (
          <span className="text-md font-medium text-tag-scheduled dark:text-tag-scheduled-dk">
            {vendor}
          </span>
        ),
        content: (
          <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
            {vendor}
          </span>
        ),
      })),
    [clientCode]
  );

  const assigneeTrigger = useMemo(() => {
    const selectedIds = formik.values.assignees ?? [];
    const resolvedAgents = selectedIds
      .map((id: string) =>
        agentsList.find(
          (a) => a?.id === id || (a as AgentUser & { _id?: string })?._id === id
        )
      )
      .filter(Boolean) as AgentUser[];

    if (resolvedAgents.length === 0) {
      return (
        <div className="flex items-center gap-[2px] group/assignee">
          <AddIcon className="text-verba-blue dark:text-verba-blue-dk w-[18px] h-[18px]" />
          <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk group-hover/assignee:underline">
            Add Assignee
          </span>
        </div>
      );
    }

    if (resolvedAgents.length === 1) {
      return (
        <div className="flex items-center gap-1.5">
          <Avatar
            text={resolvedAgents[0]?.name || ""}
            maxChars={1}
            size={22}
            className="text-xxs"
          />
          <span className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate max-w-[120px]">
            {resolvedAgents[0]?.name || "-"}
          </span>
          <div className="w-px h-[14px] bg-border-gray dark:bg-border-gray-dk" />
          <ChevronDownIcon className="w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center -space-x-1.5">
          {resolvedAgents.map((agent) => (
            <Avatar
              key={agent?.id}
              text={agent?.name || ""}
              maxChars={1}
              size={22}
              className="text-xxs ring-1 ring-pure-white dark:ring-pure-white-dk"
            />
          ))}
        </div>
        <div className="w-px h-[14px] bg-border-gray dark:bg-border-gray-dk" />
        <ChevronDownIcon className="w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
      </div>
    );
  }, [formik.values.assignees, agentsList]);

  const isFeatureRequest =
    formik?.values?.ticketCustomFieldValues?.["system-name"] ===
    FEATURE_REQUESTS_SYSTEM_NAME;

  return (
    <div
      className="flex flex-col gap-4 pt-4 px-4 pb-6 max-h-[42.5dvh] overflow-y-auto"
      data-ticket-header-scroll
    >
      <div className="flex items-center gap-2.5">
        <button
          onClick={() => navigate(-1)}
          className="shrink-0 flex items-center h-[18px] cursor-pointer"
        >
          <LeftArrowIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
        </button>

        <div className="w-full flex flex-col justify-between gap-0.5 min-w-0">
          <div className="flex items-center justify-between min-w-0">
            <TruncatedTextTooltip
              text={ticketData?.title ?? ""}
              maxWidth="max-w-full"
              className="text-lg font-medium text-verba-indigo dark:text-verba-indigo-dk min-w-0"
            />
          </div>
          <Tooltip
            content={ticketIdCopied ? "Copied!" : "Lead ID. Click to Copy"}
          >
            <button
              onClick={handleCopyTicketId}
              className="flex items-center gap-0.5 cursor-pointer w-max"
            >
              <HashtagIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
              <span className="text-md font-normal text-verba-indigo dark:text-verba-indigo-dk leading-normal">
                Ticket #{ticketData?.ticket_id ?? ticketId}
              </span>
            </button>
          </Tooltip>
        </div>

        <div className="flex items-center shrink-0">
          {/* <Tooltip content="Share">
            <button
              onClick={handleShareClick}
              className="flex items-center justify-center rounded w-[26px] h-[26px] cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            >
              <ShareIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk hover:text-verba-indigo dark:hover:text-verba-indigo-dk" />
            </button>
          </Tooltip> */}
          <Tooltip content="More">
            <button
              ref={moreButtonRef}
              data-three-dots-button
              onClick={handleMoreOptionsClick}
              className="flex items-center justify-center rounded w-[26px] h-[26px] cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            >
              <ThreeDotsVerticalIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk hover:text-verba-indigo dark:hover:text-verba-indigo-dk" />
            </button>
          </Tooltip>
        </div>
      </div>

      {ticketData?.request_support && (
        <div className="w-max flex items-center gap-2 px-2 py-1.5 rounded-lg border border-selected-option-highlight dark:border-selected-option-highlight-dk bg-card-highlight-2 dark:bg-card-highlight-2-dk">
          <div className="flex items-center gap-1 shrink-0">
            <KeyAccountManagerIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap">
              Requested Support
            </span>
          </div>
          <div className="h-[10px] w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
          <span className="text-xs font-normal text-data dark:text-data-dk leading-normal">
            Please connect with the ticket creator to discuss this ticket
          </span>
        </div>
      )}

      {ticketData?.stage_key !== "closed" && (
        <SlaDeadlineTag dueDate={ticketData?.due_date} variant="full" />
      )}

      <div
        className={`${isSidebarOpen ? "w-full" : "w-[85%]"} grid grid-cols-4 gap-x-4 gap-y-3 items-start`}
      >
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Ticket Status
          </span>
          <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
            <Dropdown
              id="ticket-status-dropdown"
              options={(stagesData ?? [])?.map((stage) => ({
                value: stage?.key,
                content: (
                  <div className="flex items-center gap-3 ml-1">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        backgroundColor: stage?.secondary_hex_code,
                        boxShadow: `0 0 0 2px white, 0 0 0 3.8px ${hexToRgba(stage?.secondary_hex_code, 0.3)}`,
                      }}
                    />
                    <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk uppercase">
                      {stage?.title}
                    </span>
                  </div>
                ),
                selectedOptionContent: (
                  <span className="text-md font-semibold text-pure-white dark:text-pure-white uppercase">
                    {stage?.title}
                  </span>
                ),
              }))}
              value={formik?.values?.stage_key}
              onChange={(val) => handleStageChange(val)}
              className="min-w-[120px] w-max"
              triggerClassName={`!rounded-md !h-[26px] ${selectedStage ? "!border-0" : ""}`}
              selectedOptionContainerClassName="!px-2 !py-0 gap-2 h-full max-w-[200px] whitespace-nowrap truncate"
              triggerIconClassName={`${selectedStage ? "!text-pure-white dark:!text-pure-white" : ""}`}
              dropdownClassName="min-w-[240px]"
              dropdownMenuTitle={
                <div className="px-3.5 pt-3 pb-2">
                  <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase tracking-wide">
                    Ticket Status
                  </p>
                  <p className="text-xs font-normal text-gray-placeholder dark:text-gray-placeholder-dk mt-0.5">
                    Select the status below you'd like to move this ticket to
                  </p>
                </div>
              }
              customTriggerStyle={{
                background: selectedStage?.secondary_hex_code,
              }}
              selectedOptionTextClassName="!text-md max-w-[200px] whitespace-nowrap truncate text-pure-white dark:text-pure-white uppercase"
              ariaLabel={helpdeskLabels.ticketStatus}
            />
          </PermissionDisabledWrapper>
        </div>

        {isResolutionEnabled ? (
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
              Resolution
            </span>
            <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
              <Dropdown
                id="ticket-resolution-dropdown"
                options={resolutionOptions}
                value={formik?.values?.resolution_reason}
                onChange={(val) => handleFieldChange("resolution_reason", val)}
                placeholder="Select resolution"
                className="w-max max-w-full"
                triggerClassName={`!rounded-lg !h-[26px] !border !max-w-full ${formik?.values?.resolution_reason ? "!border-tag-green-stroke dark:!border-tag-green-stroke-dk" : "!border-border-gray dark:!border-border-gray-dk"}`}
                selectedOptionContainerClassName="!px-2 !py-0 h-full min-w-0"
                dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk !min-w-[220px]"
                selectedOptionTextClassName="!text-md !min-w-0 !truncate"
                placeholderClassName="!text-xs !text-gray-placeholder dark:!text-gray-placeholder-dk"
                optionClassName="!border-b !border-border-gray dark:!border-border-gray-dk"
                ariaLabel="Resolution"
              />
            </PermissionDisabledWrapper>
          </div>
        ) : null}

        {isResolutionEnabled ? (
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
              External Vendor
            </span>
            <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
              <Dropdown
                Icon={HourglassIcon}
                iconClassName="w-3.5 h-3.5 text-tag-scheduled dark:text-tag-scheduled-dk shrink-0"
                id="ticket-external-vendor-dropdown"
                options={vendorOptions}
                value={formik?.values?.external_vendor}
                onChange={(val) => handleFieldChange("external_vendor", val)}
                placeholder="Select external vendor"
                className="w-max max-w-full"
                triggerClassName={`!rounded-lg !h-[26px] !border !max-w-full ${formik?.values?.external_vendor ? "!border-tag-lavender-stroke dark:!border-tag-lavender-stroke-dk" : "!border-border-gray dark:!border-border-gray-dk"}`}
                selectedOptionContainerClassName="!px-2 !py-0 h-full min-w-0 bg-tags-lavender dark:bg-tags-lavender-dk"
                dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk !min-w-[220px]"
                selectedOptionTextClassName="!text-md !min-w-0 !truncate text-tag-scheduled dark:text-tag-scheduled-dk"
                placeholderClassName="!text-xs !text-gray-placeholder dark:!text-gray-placeholder-dk"
                optionClassName="!border-b !border-border-gray dark:!border-border-gray-dk"
                ariaLabel="Resolution"
                triggerIconClassName="text-tag-scheduled dark:text-tag-scheduled-dk"
              />
            </PermissionDisabledWrapper>
          </div>
        ) : null}

        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Assigned To
          </span>
          <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
            <AgentAssignmentDropdown
              selectedAgentIds={formik.values.assignees}
              onApply={handleAssignAgent}
              isLoading={isAssigningAgent}
              agentsList={agentsList}
              subHeading={helpdeskLabels.assignTicketSubheading}
              maxAgents={4}
              hideRole={true}
              canSaveEmpty={true}
              title="ASSIGNEES"
              triggerText="Add Assignee"
              onToggleScroll={handleScrollToggle}
              customTrigger={assigneeTrigger}
            />
          </PermissionDisabledWrapper>
        </div>

        {!isFeatureRequest && (
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
              Priority
            </span>
            <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
              <Dropdown
                id="ticket-priority-dropdown"
                options={PRIORITY_FILTER_OPTIONS?.map((opt) => ({
                  value: opt?.key,
                  content: (
                    <div className="flex items-center gap-1">
                      <FlaggedIcon
                        className={`w-3 h-3 shrink-0 ${getPriorityColor(opt?.label)}`}
                      />
                      <span
                        className={`text-md font-medium ${getPriorityColor(opt?.label)}`}
                      >
                        {opt?.label}
                      </span>
                    </div>
                  ),
                  selectedOptionContent: (
                    <div className="flex items-center gap-0.5">
                      <FlaggedIcon
                        className={`w-3 h-3 shrink-0 ${getPriorityColor(opt?.label)}`}
                      />
                      <span
                        className={`text-md font-medium leading-normal ${getPriorityColor(opt?.label)}`}
                      >
                        {opt?.label}
                      </span>
                    </div>
                  ),
                }))}
                value={formik?.values?.priority}
                onChange={(val) => handlePriorityChange(val)}
                className="min-w-[120px] w-max"
                triggerClassName="!rounded-lg !h-[26px]"
                selectedOptionContainerClassName="!px-2 !py-0 h-full"
                dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk"
                selectedOptionTextClassName="!text-md"
                ariaLabel="Priority"
              />
            </PermissionDisabledWrapper>
          </div>
        )}

        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Created
          </span>
          <div className="flex items-center gap-0.5 min-w-0">
            <CalendarIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
            <TruncatedTextTooltip
              text={createdAtDisplayText}
              maxWidth="max-w-full min-w-0"
              className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk min-w-0"
            />
          </div>
        </div>

        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Created By
          </span>
          <TruncatedTextTooltip
            text={getCreatedByName(ticketData?.created_by)}
            maxWidth="max-w-full min-w-0"
            className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk min-w-0"
          />
        </div>

        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            SLA Deadline
          </span>
          <div className="flex items-center gap-1 min-w-0">
            <ClockIcon className="w-3 h-3 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
            <TruncatedTextTooltip
              text={slaDeadlineDisplayText}
              maxWidth="max-w-full min-w-0"
              className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk min-w-0"
            />
          </div>
        </div>

        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Last Updated
          </span>
          <div className="flex items-center gap-1 min-w-0">
            <ClockIcon className="w-3 h-3 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
            <TruncatedTextTooltip
              text={lastUpdatedDisplayText}
              maxWidth="max-w-full min-w-0"
              className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk min-w-0"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Ticket Category
          </span>
          <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
            <Dropdown
              id="ticket-category-dropdown"
              options={categoryOptions}
              value={formik?.values?.ticket_category}
              onChange={handleTicketCategoryChange}
              placeholder="Select category"
              className="w-max max-w-full"
              triggerClassName="group !rounded-md !h-[26px] !border-0 !bg-transparent dark:!bg-transparent !p-0"
              selectedOptionContainerClassName="!px-0 !py-0 h-full min-w-0"
              selectedOptionTextClassName="!text-md !min-w-0"
              dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk min-w-[200px]"
              optionClassName="!border-b !border-border-gray dark:!border-border-gray-dk"
              placeholderClassName="!text-xs !text-gray-placeholder dark:!text-gray-placeholder-dk"
              showChevron={false}
              ariaLabel={helpdeskLabels.ticketCategory}
            />
          </PermissionDisabledWrapper>
        </div>

        <div className="flex flex-col min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Site Name
          </span>
          <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
            <Dropdown
              id="ticket-site-name-dropdown"
              options={propertyOptions?.map(
                (opt: { key: string; label: string }) => ({
                  value: opt?.key,
                  content: (
                    <TruncatedTextTooltip
                      text={opt?.label}
                      maxWidth="max-w-full min-w-0"
                      className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk"
                    />
                  ),
                  selectedOptionContent: (
                    <TruncatedTextTooltip
                      text={opt?.label}
                      maxWidth="max-w-full min-w-0"
                      className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk min-w-0"
                    />
                  ),
                })
              )}
              value={formik?.values?.property_name}
              placeholderClassName="!text-xs !text-gray-placeholder dark:!text-gray-placeholder-dk"
              onChange={(val) => handleFieldChange("property_name", val)}
              className="w-max max-w-full relative right-1.5"
              triggerClassName="!rounded-lg !h-[26px] !bg-transparent dark:!bg-transparent !border-0 hover:!bg-hover-effect dark:hover:!bg-hover-effect-dk"
              selectedOptionContainerClassName="!px-1.5 !py-0 h-full min-w-0"
              dropdownClassName="min-w-max !bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk"
              selectedOptionTextClassName="!text-md !min-w-0"
              triggerIconClassName={
                formik?.values?.property_name
                  ? "!text-heading-text dark:!text-heading-text-dk"
                  : "!text-gray-placeholder dark:!text-gray-placeholder-dk"
              }
              ariaLabel="Site Name"
            />
          </PermissionDisabledWrapper>
        </div>

        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-normal">
            Due Date
          </span>
          <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
            <SingleDatePicker
              value={
                formik?.values?.due_date
                  ? new Date(formik?.values?.due_date)
                  : null
              }
              onChange={(date) =>
                handleFieldChange("due_date", date ? date?.toISOString() : null)
              }
              Icon={CalendarIcon}
              iconClassName="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk"
              inputClassName="w-max !gap-1 text-md font-medium text-heading-text dark:text-heading-text-dk h-[26px] rounded-lg py-0"
              placeholder="Select date"
            />
          </PermissionDisabledWrapper>
        </div>

        <HelpdeskTicketDynamicFields
          fieldDefinitions={customFieldDefs}
          values={formik?.values?.ticketCustomFieldValues}
          onFieldChange={handleTicketCustomFieldChange}
          disabled={!canUpdate}
          ticketCategory={formik?.values?.ticket_category}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {formik?.values?.tags?.map((tag: string, index: number) => (
          <div
            key={index}
            className="group flex items-center px-2 py-[3.5px] rounded border border-border-gray dark:border-border-gray-dk bg-hover-effect dark:bg-hover-effect-dk"
          >
            <TruncatedTextTooltip
              text={
                tagOptions?.find(
                  (o: { value: string; label: string }) => o.value === tag
                )?.label ?? tag
              }
              maxWidth="max-w-full min-w-0"
              className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk"
            />
            <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
              <button
                type="button"
                onClick={() => {
                  if (!canUpdate) return;
                  const newTags = formik?.values?.tags?.filter(
                    (t: string) => t !== tag
                  );
                  handleFieldChange("tags", newTags);
                }}
                className={`ml-1 hidden group-hover:flex hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-[2px] p-0.5 shrink-0 ${
                  canUpdate ? "cursor-pointer" : "cursor-not-allowed"
                }`}
              >
                <CrossCircleIcon className="w-3 h-3 text-heading-text dark:text-heading-text-dk" />
              </button>
            </PermissionDisabledWrapper>
          </div>
        ))}

        {formik?.values?.tags?.length > 0 && (
          <div className="h-3.5 w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
        )}

        <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
          <MultiSelectSearchableDropdown
            id="ticket-tags-dropdown"
            options={tagOptions}
            value={formik?.values?.tags}
            onChange={(values) => handleFieldChange("tags", values)}
            placeholder="Add Tag"
            showSelectedOptionsChips={false}
            showSearch={true}
            searchPlaceholder="Search or type to add a tag"
            className="w-max"
            triggerClassName="!rounded-md !h-[26px] !border-border-gray dark:!border-border-gray-dk hover:!bg-hover-effect dark:hover:!bg-hover-effect-dk"
            inputContainerClassName="!px-2 !py-0 h-full !gap-1"
            placeholderClassName="text-md font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap"
            triggerIconClassName="!text-heading-text dark:!text-heading-text-dk"
            dropdownClassName="min-w-[200px] !bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk"
            showChevron={false}
            Icon={AddIcon}
            iconClassName="w-4 h-4 text-heading-text dark:text-heading-text-dk shrink-0"
            ariaLabel="Add Tag"
            optionsListClassName="!py-0"
            addButtonIcon={AddIcon}
            addButtonLabel={(query) =>
              query?.trim() ? `Add '${query.trim()}' tag` : "Add new tag"
            }
            addButtonIconClassName="w-4 h-4 text-verba-blue dark:text-verba-blue-dk"
            addButtonClassName="text-verba-blue dark:text-verba-blue-dk"
            addButtonLabelClassName="text-md font-semibold"
            addButtonLoading={
              isLoadingCreateTagMutation || isFetchingHelpdeskTags
            }
            addButtonLoadingText="Adding tag..."
            onAddButtonClick={(searchQuery) => handleAddTag(searchQuery)}
          />
        </PermissionDisabledWrapper>

        <PermissionDisabledWrapper disabled={!canUpdate} className="w-max">
          <button
            ref={addCustomFieldRef}
            onClick={() => setShowCustomFieldMenu((prev) => !prev)}
            className="flex items-center gap-0.5 h-[26px] px-3.5 py-1.5 rounded-md border border-border-gray dark:border-border-gray-dk cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
          >
            <AddIcon className="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk" />
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap leading-normal">
              Add Custom Field
            </span>
          </button>
        </PermissionDisabledWrapper>
      </div>

      {showActionMenu && (
        <TicketCardActionMenu
          ticket={ticketData as HelpDeskTicket}
          triggerRef={moreButtonRef}
          onDeleteClick={handleDeleteTicket}
          onClose={() => setShowActionMenu(false)}
          onShare={() => setShowShareModal(true)}
          hideView
          canUpdate={canUpdate}
        />
      )}

      {showCustomFieldMenu && (
        <AddCustomFieldDropdownMenu
          triggerRef={addCustomFieldRef}
          onSelect={handleCustomFieldSelect}
          onClose={() => setShowCustomFieldMenu(false)}
        />
      )}

      <HelpdeskTicketCustomFieldModal
        open={customFieldModal?.open}
        fieldType={customFieldModal?.fieldType}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setCustomFieldModal({ open: false, fieldType: null });
          }
        }}
      />

      <ShareTicketModal
        ticketId={ticketData?.ticket_id ?? 0}
        open={showShareModal}
        onOpenChange={setShowShareModal}
      />

      <DeleteTicketModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirmDelete={handleConfirmDelete}
        ticket={ticketData as HelpDeskTicket}
        isDeleting={isDeleting}
      />

      <DueDateAlertModal
        open={showDueDateAlertModal}
        onOpenChange={handleCancelPriority}
        onSubmit={handleSubmitPriority}
        isLoading={isUpdatingPriority}
      />

      <TicketCategoryChangeModal
        open={Boolean(pendingCategory)}
        categoryDisplayName={pendingCategoryDisplayName}
        forcedSystemName={pendingForcedSystemName}
        systemNameField={systemNameFieldDef}
        issueTypeField={issueTypeFieldDef}
        isLoading={isUpdatingCategory}
        onCancel={handleCancelCategoryChange}
        onSubmit={handleConfirmCategoryChange}
      />

      <ResolutionReasonModal
        open={showResolutionModal}
        onSubmit={handleResolutionSubmit}
        onCancel={handleResolutionCancel}
        isLoading={isSubmittingResolution}
        initialValue={formik?.values?.resolution_reason}
      />

      <WaitingOnThirdPartyModal
        open={showThirdPartyModal}
        onSubmit={handleThirdPartySubmit}
        onCancel={handleThirdPartyCancel}
        isLoading={isSubmittingThirdParty}
        initialValue={formik?.values?.external_vendor}
      />

      <AssigneeRequiredModal
        open={showAssigneeRequiredModal}
        onClose={handleAssigneeRequiredClose}
      />
    </div>
  );
};
